import { useState, useEffect } from 'react';
import { useCart } from '@/context/CartContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, CheckCircle2, Loader2, CreditCard, Smartphone, Wallet, Banknote, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import GlutenFreeIcon from '@/components/GlutenFreeIcon';
import { PaymentMethod as OrderPaymentMethod } from '@/types/order';
import PageSEO from '@/components/PageSEO';

type PaymentMethod = 'online' | 'pickup';

// Generate pickup time slots from 17:40 to 21:50 in 10-minute intervals
const generateTimeSlots = () => {
  const slots: string[] = [];
  for (let hour = 17; hour <= 21; hour++) {
    for (let minute = 0; minute < 60; minute += 10) {
      if (hour === 17 && minute < 40) continue;
      const timeStr = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      slots.push(timeStr);
    }
  }
  return slots;
};

const TIME_SLOTS = generateTimeSlots();

interface SlotAvailability {
  [slot: string]: { count: number; isFull: boolean };
}

const Checkout = () => {
  const { items, totalPrice, clearCart } = useCart();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('pickup');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState<{ shortCode: string; token: string; pendingPayment?: boolean } | null>(null);
  const [ordersEnabled, setOrdersEnabled] = useState<boolean | null>(null);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const [slotAvailability, setSlotAvailability] = useState<SlotAvailability>({});
  const [maxOrdersPerSlot, setMaxOrdersPerSlot] = useState<number>(5);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    pickupTime: '',
    notes: '',
  });

  // Check if orders are enabled and fetch slot availability
  useEffect(() => {
    const fetchSettingsAndAvailability = async () => {
      try {
        // Fetch settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('app_settings')
          .select('orders_enabled, max_orders_per_slot')
          .eq('id', 'main')
          .maybeSingle();
        
        if (settingsError) throw settingsError;
        setOrdersEnabled(settingsData?.orders_enabled ?? true);
        const maxOrders = settingsData?.max_orders_per_slot ?? 5;
        setMaxOrdersPerSlot(maxOrders);

        // Fetch today's orders to calculate slot availability
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);

        const { data: ordersData, error: ordersError } = await supabase
          .from('orders')
          .select('pickup_time')
          .gte('pickup_time', today.toISOString())
          .lt('pickup_time', tomorrow.toISOString())
          .not('status', 'eq', 'annullato');

        if (ordersError) throw ordersError;

        // Count orders per slot
        const slotCounts: SlotAvailability = {};
        TIME_SLOTS.forEach(slot => {
          slotCounts[slot] = { count: 0, isFull: false };
        });

        (ordersData || []).forEach(order => {
          if (order.pickup_time) {
            const time = new Date(order.pickup_time);
            // Use UTC to match how times are stored in the database
            const timeStr = `${time.getUTCHours().toString().padStart(2, '0')}:${time.getUTCMinutes().toString().padStart(2, '0')}`;
            if (slotCounts[timeStr]) {
              slotCounts[timeStr].count++;
            }
          }
        });

        // Mark full slots
        Object.keys(slotCounts).forEach(slot => {
          slotCounts[slot].isFull = slotCounts[slot].count >= maxOrders;
        });

        setSlotAvailability(slotCounts);
      } catch (err) {
        console.error('Error checking settings:', err);
        setOrdersEnabled(true);
      } finally {
        setCheckingSettings(false);
      }
    };
    
    fetchSettingsAndAvailability();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleTimeChange = (value: string) => {
    // Block selection of full slots
    const availability = slotAvailability[value];
    if (availability?.isFull) {
      toast.error('Questo orario è al completo');
      return;
    }
    setFormData(prev => ({ ...prev, pickupTime: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error('Inserisci nome, email e numero di telefono');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      toast.error('Inserisci un indirizzo email valido');
      return;
    }

    if (!formData.pickupTime) {
      toast.error('Seleziona un orario di ritiro');
      return;
    }

    // Double-check slot availability before submitting
    const selectedSlot = slotAvailability[formData.pickupTime];
    if (selectedSlot?.isFull) {
      toast.error('Questo orario è al completo, selezionane un altro');
      setFormData(prev => ({ ...prev, pickupTime: '' }));
      return;
    }

    if (items.length === 0) {
      toast.error('Il carrello è vuoto');
      return;
    }

    setIsSubmitting(true);

    // Open window immediately for Stripe payments (before async call to avoid popup blocker)
    let stripeWindow: Window | null = null;
    if (paymentMethod === 'online') {
      stripeWindow = window.open('about:blank', '_blank');
    }

    try {
      // For 'pickup', we use 'contanti' as placeholder - admin will mark actual method at pickup
      const dbPaymentMethod: OrderPaymentMethod = paymentMethod === 'online' ? 'stripe' : 'contanti';

      // Build pickup time as today's date with selected time (in UTC)
      let pickupTime: string | null = null;
      if (formData.pickupTime) {
        const today = new Date();
        const [hours, minutes] = formData.pickupTime.split(':').map(Number);
        // Use UTC to ensure consistent storage
        today.setUTCHours(hours, minutes, 0, 0);
        pickupTime = today.toISOString();
      }

      // Prepare order items
      const orderItems = items.map(item => ({
        product_id: null, // We're using local menu data, not DB products
        name_snapshot: item.pizza.name,
        unit_price_cents: Math.round(item.pizza.price * 100),
        quantity: item.quantity,
        gluten_option: item.isGlutenFree ? 'senza_glutine' : 'con_glutine',
      }));

      const { data, error } = await supabase.functions.invoke('create-order', {
        body: {
          customer_name: formData.name.trim(),
          customer_email: formData.email.trim(),
          customer_phone: formData.phone.trim(),
          pickup_time: pickupTime,
          order_note: formData.notes.trim() || null,
          payment_method: dbPaymentMethod,
          items: orderItems,
        },
      });

      if (error) {
        stripeWindow?.close();
        throw error;
      }

      // Send confirmation email (non-blocking)
      supabase.functions.invoke('send-order-email', {
        body: {
          order_id: data.order_id,
          token: data.customer_access_token,
          origin: window.location.origin,
        },
      }).catch(console.error);

      // If Stripe payment, redirect the pre-opened window
      if (data.checkout_url && stripeWindow) {
        stripeWindow.location.href = data.checkout_url;
        clearCart();
        setOrderResult({
          shortCode: data.short_code,
          token: data.customer_access_token,
          pendingPayment: true,
        });
        return;
      }

      // For pickup payments, show success
      setOrderResult({
        shortCode: data.short_code,
        token: data.customer_access_token,
      });
      clearCart();
      toast.success('Ordine confermato!');

    } catch (err: any) {
      console.error('Order error:', err);
      toast.error(err.message || 'Errore nella creazione dell\'ordine');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="h-10 w-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-4">
            {orderResult.pendingPayment ? 'Completa il Pagamento' : 'Ordine Confermato!'}
          </h1>
          
          <div className="card-elevated p-6 mb-6">
            <p className="text-sm text-muted-foreground mb-2">Il tuo codice ordine:</p>
            <p className="font-mono text-3xl font-bold text-primary tracking-widest">
              {orderResult.shortCode}
            </p>
          </div>

          <p className="text-muted-foreground mb-8">
            {orderResult.pendingPayment 
              ? 'Completa il pagamento nella scheda aperta. Riceverai un\'email di conferma.'
              : 'Riceverai un\'email con i dettagli. Ricorda che il pagamento sarà al ritiro.'}
          </p>
          
          <div className="space-y-3">
            <Button 
              variant="hero" 
              className="w-full"
              onClick={() => navigate(`/ordine?token=${orderResult.token}`)}
            >
              Traccia il tuo ordine
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate('/')}>
              Torna alla Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading settings
  if (checkingSettings) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Orders disabled
  if (ordersEnabled === false) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
            <Clock className="h-10 w-10 text-yellow-500" />
          </div>
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Ordini temporaneamente sospesi
          </h1>
          <p className="text-muted-foreground mb-8">
            Al momento non è possibile effettuare ordini online. Riprova più tardi o contattaci direttamente.
          </p>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna al Menù
          </Button>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground mb-4">
            Il carrello è vuoto
          </h1>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Torna al Menù
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <PageSEO
        title="Checkout – Pizzeria La Torre, Carmagnola (TO)"
        description="Completa il tuo ordine di pizza da asporto presso Pizzeria La Torre a Carmagnola (TO). Scegli orario di ritiro e metodo di pagamento."
        noindex
      />
      <div className="container mx-auto px-4 max-w-4xl">
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-5 w-5" />
          Torna al Menù
        </button>

        <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
          Completa l'Ordine
        </h1>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Contact Info */}
              <div className="card-elevated p-6 space-y-4">
                <h2 className="text-xl font-semibold text-foreground">I tuoi dati</h2>
                
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input id="name" name="name" placeholder="Il tuo nome" value={formData.name} onChange={handleInputChange} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input id="email" name="email" type="email" placeholder="La tua email" value={formData.email} onChange={handleInputChange} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono *</Label>
                  <Input id="phone" name="phone" type="tel" placeholder="Il tuo numero di telefono" value={formData.phone} onChange={handleInputChange} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pickupTime">Orario di ritiro *</Label>
                  <Select value={formData.pickupTime} onValueChange={handleTimeChange}>
                    <SelectTrigger id="pickupTime" className="w-full">
                      <SelectValue placeholder="Seleziona un orario" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((time) => {
                        const availability = slotAvailability[time];
                        const isFull = availability?.isFull;
                        return (
                          <SelectItem 
                            key={time} 
                            value={time} 
                            disabled={isFull}
                            className={isFull ? 'opacity-50' : ''}
                          >
                            {time} {isFull && <span className="text-xs text-muted-foreground ml-2">(Al completo)</span>}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Note aggiuntive</Label>
                  <textarea
                    id="notes"
                    name="notes"
                    placeholder="Eventuali richieste..."
                    value={formData.notes}
                    onChange={handleInputChange}
                    className="w-full h-24 px-3 py-2 rounded-lg bg-muted border border-border text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                  />
                </div>
              </div>

              {/* Payment Method */}
              <div className="card-elevated p-6 space-y-4">
                <h2 className="text-xl font-semibold text-foreground">Quando preferisci pagare?</h2>

                <div className="grid grid-cols-2 gap-4">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('online')}
                    className={`p-4 rounded-xl border-2 transition-all ${paymentMethod === 'online' ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground'}`}
                  >
                    <CreditCard className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'online' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className={`text-sm font-medium ${paymentMethod === 'online' ? 'text-foreground' : 'text-muted-foreground'}`}>Paga Ora</p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPaymentMethod('pickup')}
                    className={`p-4 rounded-xl border-2 transition-all ${paymentMethod === 'pickup' ? 'border-primary bg-primary/10' : 'border-border hover:border-muted-foreground'}`}
                  >
                    <Wallet className={`h-6 w-6 mx-auto mb-2 ${paymentMethod === 'pickup' ? 'text-primary' : 'text-muted-foreground'}`} />
                    <p className={`text-sm font-medium ${paymentMethod === 'pickup' ? 'text-foreground' : 'text-muted-foreground'}`}>Paga al Ritiro</p>
                  </button>
                </div>

                {/* Payment Methods Info */}
                <div className="bg-muted/50 rounded-xl p-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    {paymentMethod === 'online' ? 'Metodi di pagamento online:' : 'Metodi di pagamento al ritiro:'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg text-sm border border-border">
                      <CreditCard className="h-3.5 w-3.5" />
                      Carta
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg text-sm border border-border">
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                      </svg>
                      Apple Pay
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg text-sm border border-border">
                      <span className="font-semibold text-xs">G</span>
                      Google Pay
                    </span>
                    {paymentMethod === 'pickup' && (
                      <>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg text-sm border border-border">
                          <span>◆</span>
                          Satispay
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-background rounded-lg text-sm border border-border">
                          <Banknote className="h-3.5 w-3.5" />
                          Contanti
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {paymentMethod === 'online' && (
                  <p className="text-sm text-muted-foreground text-center">
                    Verrai reindirizzato a Stripe per completare il pagamento in sicurezza.
                  </p>
                )}
              </div>

              <Button type="submit" variant="hero" size="lg" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Elaborazione...
                  </>
                ) : (
                  `Conferma Ordine - €${totalPrice.toFixed(2)}`
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="card-elevated p-6 sticky top-4">
              <h2 className="text-xl font-semibold text-foreground mb-4">Riepilogo Ordine</h2>

              <div className="space-y-4 mb-6">
                {items.map(item => (
                  <div key={item.id} className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                      <span className="text-xl">🍕</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground truncate">
                        {item.pizza.name}
                        {item.isGlutenFree && (
                          <span className="inline-flex items-center gap-1 text-xs ml-2 px-1.5 py-0.5 rounded badge-gf">
                            <GlutenFreeIcon className="h-2.5 w-2.5" />
                            GF
                          </span>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">x{item.quantity}</p>
                    </div>
                    <p className="font-medium text-foreground">€{(item.pizza.price * item.quantity).toFixed(2)}</p>
                  </div>
                ))}
              </div>

              <div className="border-t border-border pt-4">
                <div className="flex items-center justify-between text-lg">
                  <span className="font-medium text-foreground">Totale</span>
                  <span className="font-bold text-primary text-xl">€{totalPrice.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
